import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import useModal from '../../hooks/custom/useModal';
import { useGetModelsPortfolioById } from '../../hooks/useGetModelsPortfolioById';
import { modelsPortfolioChartById } from '../../utils/modelsPortfolioChart';
import { onRefresh } from '../../utils/refreshPage';
import { numberWithPoint } from '../../utils/utils';
import { ButtonsPrimary } from '../ComponentApp/buttoms';
import Spinner from '../Spinner';
import ModalErrorFetch from '../UI/ModalErrorFetch';
import ChartDoughnutXcala from '../XcalaComponents/ChartDoughnutXcala';
import { ContributionsSectionTitleForm } from './SimulationComponents/ContributionsSectionTitleForm';
import {
  BackArrowContainer,
  ChartComponent,
  ContainerForm,
  ContentFormContributions,
  ImgStyled,
  TextStyled,
} from './SimulationComponents/SimulationMuiComponents';

const SimulatorFormPortfolio = ({ handleSubmit, formData, riskPortfolio }) => {
  const { id } = useParams();
  const theme = useTheme();
  const { isShowing, toggle } = useModal();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: dataPortolioById,
    loading: loadingDataPortolioById,
    error: errorDataPortolioById,
  } = useGetModelsPortfolioById({
    modelId: id,
  });

  const dataPortfolioSimulationById = dataPortolioById?.getModelById || [];

  const dataPortfolioModels = modelsPortfolioChartById(dataPortfolioSimulationById);

  const dataChartGraph = dataPortfolioModels[0];

  const resultChartData = dataChartGraph?.dataChart || {
    labels: [],
    datasets: [],
  };
  const { labels, datasets } = resultChartData;

  const initialValues = {
    initialInvestment: '',
    monthlyInvestment: '',
    months: '1 año' || formData.months,
  };

  const validationSchema = Yup.object({
    initialInvestment: Yup.string().required('El campo es requerido'),
    monthlyInvestment: Yup.string(),
    months: Yup.string().required('El campo es requerido'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validate: values => {
      const errors = {};

      if (
        !values.initialInvestment ||
        Number.parseInt(values.initialInvestment.replace(/\D/g, '')) < 200000 ||
        values.initialInvestment[0] === '0'
      ) {
        errors.initialInvestment = 'El monto debe ser mayor a $200.000';
      }

      if (Number.parseInt(values.monthlyInvestment?.replace(/\D/g, '')) < 200000) {
        errors.monthlyInvestment = 'El aporte mensual debe ser mayor a $200.000';
      }

      return errors;
    },
  });

  const optionsYears = [{ value: '1 año' }, { value: '3 años' }, { value: '5 años' }];

  if (loadingDataPortolioById) {
    return <Spinner />;
  }

  if (errorDataPortolioById) {
    return (
      <ModalErrorFetch
        message='Lo sentimos, algo no salio bien acá....'
        error={errorDataPortolioById}
        open={!isShowing}
        onClose={toggle}
        refresh={onRefresh}
      />
    );
  }

  return (
    <>
      <BackArrowContainer>
        <IconButton
          onClick={() => navigate(-1)}
          disableRipple
          sx={{
            padding: 0,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}>
          <ImgStyled src='/assets/images/Arrow1.svg' />
        </IconButton>
      </BackArrowContainer>

      <ContentFormContributions
        sx={{
          maxWidth: {
            xs: '100%',
            sm: '100%',
            md: '70%',
          },
        }}>
        <ContributionsSectionTitleForm textTitle={' Simula tu inversión en el portfolio'} />
        <ContainerForm
          sx={{
            flexDirection: { lg: 'row', md: 'column' },
            alignItems: 'center',
            marginTop: '20px',
            gap: { xs: '32px', md: '16px' },
          }}>
          <Box
            sx={{
              width: {
                lg: '40%',
                md: '100%',
              },
              minHeight: {
                xs: 300,
                sm: 320,
              },
              display: 'flex',
              justifyContent: 'center',
            }}>
            <ChartComponent>
              <ChartDoughnutXcala dataChart={{ labels, datasets }} />
            </ChartComponent>
          </Box>

          <Box
            sx={{
              width: {
                lg: '60%',
                md: '100%',
              },
            }}>
            <Box>
              <TextField
                name='initialInvestment'
                label={
                  <Typography
                    sx={{
                      fontSize: theme.typography.mediumText.fontSize,
                      [theme.breakpoints.down('sm')]: {
                        fontSize: theme.typography.customButton.fontSize,
                      },
                    }}>
                    Inversión Inicial
                  </Typography>
                }
                variant='standard'
                fullWidth
                helperText={formik.touched.initialInvestment && formik.errors.initialInvestment}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                }}
                error={Boolean(formik.touched.initialInvestment && formik.errors.initialInvestment)}
                value={formik.values.initialInvestment || ''}
                onBlur={formik.handleBlur}
                onChange={e => {
                  const value = e.target.value;
                  const validAmount = value.replace(/\D/g, '');
                  if (validAmount.toString().length <= 8) {
                    const format = numberWithPoint(validAmount);
                    formik.setFieldValue('initialInvestment', format);
                  }
                }}
              />
              <TextStyled
                sx={{
                  width: '100%',
                }}>
                Inversión mínima del portfolio es de $200.000
              </TextStyled>
              <TextField
                sx={{
                  mb: '2px',
                }}
                name='monthlyInvestment'
                label={
                  <Typography
                    sx={{
                      fontSize: theme.typography.mediumText.fontSize,
                      [theme.breakpoints.down('sm')]: {
                        fontSize: theme.typography.customButton.fontSize,
                      },
                    }}>
                    Aporte Mensual (Opcional)
                  </Typography>
                }
                variant='standard'
                fullWidth
                error={Boolean(formik.touched.monthlyInvestment && formik.errors.monthlyInvestment)}
                onBlur={formik.handleBlur}
                helperText={formik.touched.monthlyInvestment && formik.errors.monthlyInvestment}
                onChange={e => {
                  const value = e.target.value;
                  const validAmount = value.replace(/\D/g, '');
                  if (validAmount.toString().length <= 8) {
                    const format = numberWithPoint(validAmount);
                    formik.setFieldValue('monthlyInvestment', format);
                  }
                }}
                value={formik.values.monthlyInvestment || ''}
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <FormControl variant='standard' sx={{ width: '100%' }}>
                <InputLabel id='demo-simple-select-standard-label'>
                  Plazo estimado de inversión (horizonte)
                </InputLabel>
                <Select
                  label='Age'
                  sx={{
                    width: '100%',
                    fontSize: theme.typography.availableBalance.fontSize,
                    color: theme.palette.textLight.main,
                  }}
                  id='months'
                  value={formik.values.months}
                  variant='standard'
                  onChange={e => {
                    formik.setFieldValue('months', e.target.value);
                  }}>
                  {optionsYears
                    ?.filter(item => item !== undefined)
                    .map(item => {
                      return (
                        <MenuItem key={item.value} value={item.value}>
                          {item.value}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
              {formik.touched.months && formik.errors.months ? (
                <Box
                  sx={{
                    color: theme.palette.red.main,
                    ml: 1,
                  }}>
                  {formik.errors.months}
                </Box>
              ) : null}
            </Box>
            <Box
              sx={{
                display: ' flex',
                justifyContent: { md: 'start', xs: 'center' },
              }}>
              <ButtonsPrimary
                sx={{
                  marginTop: '16px',
                  width: '100%',
                  fontSize: '17px',
                  justifyContent: 'center',
                }}
                name='Calcular'
                disableElevation
                disabled={!(formik.dirty && formik.isValid)}
                onClick={() => handleSubmit(formik.values, riskPortfolio)}
                fullWidth
              />
            </Box>
          </Box>
        </ContainerForm>
      </ContentFormContributions>
    </>
  );
};

export default SimulatorFormPortfolio;
